/**
 * Shared helpers for Word tool executors.
 */

import { asOfficeError, officeErrorLocation } from "@/lib/office/office-error";

export function formatOfficeError(err: unknown): string {
  const office = asOfficeError(err);
  if (office) {
    const at = officeErrorLocation(office);
    switch (office.code) {
      case "InvalidArgument":
        return `Invalid argument${at}. Please check the inputs.`;
      case "ItemNotFound":
        return `The specified item was not found in the document${at}.`;
      case "InvalidOperation":
        return `This operation is not supported on the current selection or target${at}.`;
      case "GeneralException":
        return `Word general exception${at}: ${office.message}`;
      default:
        return `Word error (${office.code})${at}: ${office.message}`;
    }
  }
  return err instanceof Error ? err.message : "Unknown error";
}

const INSERT_LOCATION_MAP: Record<string, Word.InsertLocation> = {
  start: "Start" as Word.InsertLocation,
  end: "End" as Word.InsertLocation,
  before: "Before" as Word.InsertLocation,
  after: "After" as Word.InsertLocation,
  replace: "Replace" as Word.InsertLocation,
};

export function toInsertLocation(value: unknown): Word.InsertLocation {
  if (typeof value !== "string" || !(value in INSERT_LOCATION_MAP)) {
    throw new Error(
      `Invalid location "${String(value)}". Expected one of: start, end, before, after, replace.`
    );
  }
  return INSERT_LOCATION_MAP[value] as Word.InsertLocation;
}

const BREAK_TYPE_MAP: Record<string, Word.BreakType> = {
  page: "Page" as Word.BreakType,
  line: "Line" as Word.BreakType,
  sectionContinuous: "SectionContinuous" as Word.BreakType,
  sectionNextPage: "SectionNextPage" as Word.BreakType,
};

export function toBreakType(value: unknown): Word.BreakType {
  if (typeof value !== "string" || !(value in BREAK_TYPE_MAP)) {
    throw new Error(
      `Invalid breakType "${String(value)}". Expected one of: page, line, sectionContinuous, sectionNextPage.`
    );
  }
  return BREAK_TYPE_MAP[value] as Word.BreakType;
}

const ALIGNMENT_MAP: Record<string, Word.Alignment> = {
  left: "Left" as Word.Alignment,
  centered: "Centered" as Word.Alignment,
  right: "Right" as Word.Alignment,
  justified: "Justified" as Word.Alignment,
};

export function toAlignment(value: unknown): Word.Alignment {
  if (typeof value !== "string" || !(value in ALIGNMENT_MAP)) {
    throw new Error(
      `Invalid alignment "${String(value)}". Expected one of: left, centered, right, justified.`
    );
  }
  return ALIGNMENT_MAP[value] as Word.Alignment;
}

const UNDERLINE_MAP: Record<string, Word.UnderlineType> = {
  none: "None" as Word.UnderlineType,
  single: "Single" as Word.UnderlineType,
  double: "Double" as Word.UnderlineType,
  dotted: "Dotted" as Word.UnderlineType,
  dashed: "DashLine" as Word.UnderlineType,
  wavy: "Wave" as Word.UnderlineType,
};

export function toUnderline(value: unknown): Word.UnderlineType {
  if (typeof value !== "string" || !(value in UNDERLINE_MAP)) {
    throw new Error(
      `Invalid underline "${String(value)}". Expected one of: none, single, double, dotted, dashed, wavy.`
    );
  }
  return UNDERLINE_MAP[value] as Word.UnderlineType;
}

/**
 * Resolve a paragraph by zero-based index. Loads `paragraphs.items` and
 * syncs — the caller is then free to set properties / call methods on
 * the returned proxy object before the next sync.
 */
export async function getParagraphAt(
  ctx: Word.RequestContext,
  index: number
): Promise<Word.Paragraph> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`Invalid paragraphIndex ${index}.`);
  }
  const paragraphs = ctx.document.body.paragraphs;
  paragraphs.load("items");
  await ctx.sync();
  const item = paragraphs.items[index];
  if (!item) {
    throw new Error(
      `paragraphIndex ${index} is out of range (document has ${paragraphs.items.length} paragraphs).`
    );
  }
  return item;
}

export function getString(
  args: Record<string, unknown>,
  key: string,
  required?: true
): string;
export function getString(
  args: Record<string, unknown>,
  key: string,
  required: false
): string | undefined;
export function getString(
  args: Record<string, unknown>,
  key: string,
  required = true
): string | undefined {
  const value = args[key];
  if (value == null) {
    if (required) {
      throw new Error(`Missing required argument: ${key}`);
    }
    return undefined;
  }
  if (typeof value !== "string") {
    throw new Error(`Argument ${key} must be a string.`);
  }
  return value;
}

export function getNumber(
  args: Record<string, unknown>,
  key: string,
  required?: true
): number;
export function getNumber(
  args: Record<string, unknown>,
  key: string,
  required: false
): number | undefined;
export function getNumber(
  args: Record<string, unknown>,
  key: string,
  required = true
): number | undefined {
  const value = args[key];
  if (value == null) {
    if (required) {
      throw new Error(`Missing required argument: ${key}`);
    }
    return undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Argument ${key} must be a finite number.`);
  }
  return value;
}

export function getBool(
  args: Record<string, unknown>,
  key: string
): boolean | undefined {
  const value = args[key];
  if (value == null) {
    return undefined;
  }
  if (typeof value !== "boolean") {
    throw new Error(`Argument ${key} must be a boolean.`);
  }
  return value;
}
